import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Save, Plus, CreditCard, Loader2, Headphones, BookOpen, Gift, Settings2, LayoutList } from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader, Field } from './_shared';
import { TokenPacksCard, CopyscapePacksCard } from './PricingPanelPackCards';
import FreeTierSunsetBanner from '@/components/FreeTierSunsetBanner';
import { PromoAdminCard } from '@/components/admin/PromoAdminCard';
import { TierFeatureMap } from '@/components/admin/TierFeatureMap';
import { TierFeaturesPopup } from '@/components/admin/TierFeaturesPopup';

// Stable client-side row identity used purely as the React `key` for
// editable token-pack rows. We can't key by the pack's `id` field
// because the user edits it inline — keying by `p.id` would unmount
// the input on every keystroke and kill focus. `_uid` is stripped
// from the payload sent to `PUT /api/admin/config`.
//
// Hoisted to module scope (was missing entirely after an earlier edit
// landed only its import block) so `_newUid()` is defined at both call
// sites — see `reload()` at line ~28 and `addRow()` at line ~67.
const _newUid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `uid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

export default function PricingPanel() {
  const [cfg, setCfg] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [csPacks, setCsPacks] = useState([]);           // iter55p — Copyscape ladder
  const [tokensPerUsd, setTokensPerUsd] = useState(60000);
  const [markupPct, setMarkupPct] = useState(0);  // iter-17
  const [busy, setBusy] = useState(false);
  // iter-19 — Email receipts + yearly promo (admin-configurable)
  const [receiptsEnabled, setReceiptsEnabled] = useState(false);
  const [receiptsBrand, setReceiptsBrand] = useState('The Loom V2.38_f55');
  const [receiptsColor, setReceiptsColor] = useState('#18181b');
  const [receiptsSubject, setReceiptsSubject] = useState('The Loom · Receipt');
  const [receiptsFooter, setReceiptsFooter] = useState('');
  const [promoEnabled, setPromoEnabled] = useState(true);
  const [promoLabel, setPromoLabel] = useState('');
  // iter-51r — Canva pay-as-you-go pricing.
  const [canvaCreate, setCanvaCreate] = useState(5);
  const [canvaExport, setCanvaExport] = useState(10);
  const [canvaMarkup, setCanvaMarkup] = useState(20);
  const [canvaSignupFree, setCanvaSignupFree] = useState(1);   // iter-51t
  // 2026-10 — Audiobook unlock price (pay to unlock the rest of a
  // sample-limited audiobook job's chapters).
  const [audiobookUnlockPrice,      setAudiobookUnlockPrice]      = useState(14.99);
  // Feb-2026 COGS audit — Production price + BYOK fee (all DB-overridable)
  const [audiobookProductionPrice,  setAudiobookProductionPrice]  = useState(34.99);
  const [audiobookByokFee,          setAudiobookByokFee]          = useState(12.99);
  const [audiobookFishPrice,        setAudiobookFishPrice]        = useState(24.99);
  // Canva user-facing cover price (distinct from the API cost mark-up below)
  const [canvaCoverPrice,           setCanvaCoverPrice]           = useState(4.99);
  // 2026-10 — POD length surcharge (same override treatment as the
  // audiobook unlock price). Stored nested under bookstore_marketing.*.
  const [podSurchargeThreshold, setPodSurchargeThreshold] = useState(550);
  const [podSurchargeRate, setPodSurchargeRate] = useState(0.03);
  // iter55z-i — Community Pulse (social-proof widget) admin toggles.
  const [pulseEnabled, setPulseEnabled] = useState(true);
  const [pulseMode, setPulseMode] = useState('anonymized');
  // iter-52a — storefront default currency.
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [supportedCurrencies, setSupportedCurrencies] = useState([]);
  // iter-payments-master — Big ON/OFF kill switch for Stripe. Wired to
  // `integrations_enabled.stripe` — the same flag `require_integration`
  // guards every checkout endpoint against.
  const [stripeOn, setStripeOn] = useState(true);
  const [stripeSaving, setStripeSaving] = useState(false);
  const [downtimeReason, setDowntimeReason] = useState('');
  const [downtimeSaving, setDowntimeSaving] = useState(false);
  // 2026-08 — Free-tier sunset date (admin-editable). New accounts
  // created on/after this date get no free LLM allowance at all —
  // existing free accounts are untouched. Blank = no sunset, Free never ends.
  const [freeTierSunsetDate, setFreeTierSunsetDate] = useState('');
  // iter-promo — Tier features popup state
  const [featuresPopupTier, setFeaturesPopupTier] = useState(null);

  useEffect(() => {
    import('axios').then(({ default: axios }) => {
      const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      axios.get(`${API}/currency/supported`)
        .then(r => setSupportedCurrencies(r.data?.currencies || []))
        .catch(() => {});
    });
  }, []);

  const reload = async () => {
    try {
      const r = await adminApi.config();
      setCfg(r);
      setPricing((r.pricing || []).map((p) => ({ ...p, _uid: p._uid || _newUid() })));
      setCsPacks((r.copyscape_packs || []).map((p) => ({ ...p, _uid: p._uid || _newUid() })));
      setTokensPerUsd(r.tokens_per_usd || 60000);
      setMarkupPct(r.pricing_markup_pct ?? 0);
      setReceiptsEnabled(r.email_receipts_enabled ?? false);
      setReceiptsBrand(r.email_receipts_brand_name || 'The Loom V2.38_f55');
      setReceiptsColor(r.email_receipts_primary_color || '#18181b');
      setReceiptsSubject(r.email_receipts_subject_prefix || 'The Loom · Receipt');
      setReceiptsFooter(r.email_receipts_footer_html || '');
      setPromoEnabled(r.yearly_promo_enabled ?? true);
      setPromoLabel(r.yearly_promo_label || '');
      setCanvaCreate(r.canva_design_create_cents ?? 5);
      setCanvaExport(r.canva_export_cents ?? 10);
      setCanvaMarkup(r.canva_markup_pct ?? 20);
      setCanvaSignupFree(r.canva_signup_free_covers ?? 1);
      setAudiobookUnlockPrice(r.audiobook_unlock_price_usd ?? 14.99);
      setAudiobookProductionPrice(r.audiobook_production_price_usd ?? 34.99);
      setAudiobookByokFee(r.audiobook_byok_fee_usd ?? 12.99);
      setAudiobookFishPrice(r.audiobook_fish_price_usd ?? 24.99);
      setCanvaCoverPrice(r.canva_cover_price_usd ?? 4.99);
      setPodSurchargeThreshold(r?.bookstore_marketing?.pod_length_surcharge_threshold_pages ?? 550);
      setPodSurchargeRate(r?.bookstore_marketing?.pod_length_surcharge_per_page_usd ?? 0.03);
      setPulseEnabled(r.canva_pulse_enabled ?? true);
      setPulseMode((r.canva_pulse_mode || 'anonymized').toLowerCase());
      setDefaultCurrency((r.default_currency || 'USD').toUpperCase());
      setStripeOn(r?.integrations_enabled?.stripe ?? true);
      setDowntimeReason(r?.payments_downtime_reason || '');
      setFreeTierSunsetDate(r.free_tier_sunset_date || '');
    } catch { toast.error('Could not load config'); }
  };

  const saveDowntimeReason = async () => {
    if (downtimeSaving) return;
    setDowntimeSaving(true);
    try {
      await adminApi.saveConfig({ payments_downtime_reason: downtimeReason.trim() });
      toast.success('Downtime reason saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setDowntimeSaving(false); }
  };

  const togglePayments = async () => {
    if (stripeSaving) return;
    const next = !stripeOn;
    setStripeOn(next);
    setStripeSaving(true);
    try {
      await adminApi.saveConfig({ integrations_enabled: { stripe: next } });
      toast.success(`Payment processing ${next ? 'ENABLED' : 'DISABLED'}.`);
      reload();
    } catch (e) {
      setStripeOn(!next);
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setStripeSaving(false); }
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({
        // Strip the client-only _uid row identity before persisting.
        pricing: pricing.map(({ _uid: _drop, ...rest }) => rest),
        // iter55p — Copyscape ladder saved as `copyscape_packs`.
        copyscape_packs: csPacks.map(({ _uid: _drop, ...rest }) => rest),
        tokens_per_usd: tokensPerUsd,
        pricing_markup_pct: Math.max(0, Math.min(100, parseInt(markupPct, 10) || 0)),
        email_receipts_enabled: receiptsEnabled,
        email_receipts_brand_name: receiptsBrand,
        email_receipts_primary_color: receiptsColor,
        email_receipts_subject_prefix: receiptsSubject,
        email_receipts_footer_html: receiptsFooter,
        yearly_promo_enabled: promoEnabled,
        yearly_promo_label: promoLabel,
        canva_design_create_cents: Math.max(0, Math.min(10000, parseInt(canvaCreate, 10) || 0)),
        canva_export_cents:        Math.max(0, Math.min(10000, parseInt(canvaExport, 10) || 0)),
        canva_markup_pct:          Math.max(0, Math.min(200,   parseInt(canvaMarkup, 10) || 0)),
        canva_signup_free_covers:  Math.max(0, Math.min(100,    parseInt(canvaSignupFree, 10) || 0)),
        audiobook_unlock_price_usd:      Math.max(0, Math.min(500,  parseFloat(audiobookUnlockPrice) || 0)),
        audiobook_production_price_usd:  Math.max(0, Math.min(999,  parseFloat(audiobookProductionPrice) || 34.99)),
        audiobook_byok_fee_usd:          Math.max(0, Math.min(999,  parseFloat(audiobookByokFee) || 12.99)),
        audiobook_fish_price_usd:        Math.max(0, Math.min(999,  parseFloat(audiobookFishPrice) || 24.99)),
        canva_cover_price_usd:           Math.max(0, Math.min(999,  parseFloat(canvaCoverPrice) || 4.99)),
        pod_length_surcharge_threshold_pages: Math.max(0, Math.min(5000, parseInt(podSurchargeThreshold, 10) || 0)),
        pod_length_surcharge_per_page_usd:    Math.max(0, Math.min(5,    parseFloat(podSurchargeRate) || 0)),
        canva_pulse_enabled:       !!pulseEnabled,
        canva_pulse_mode:          pulseMode,
        default_currency: defaultCurrency,
        free_tier_sunset_date: freeTierSunsetDate.trim(),
      });
      toast.success('Pricing saved.');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const updateRow = (idx, patch) => {
    setPricing((arr) => arr.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };
  const addRow = () => setPricing((arr) => [...arr, { _uid: _newUid(), id: `pack_${arr.length + 1}`, name: 'New pack', tokens: 1_000_000, price: 0, desc: '' }]);
  const removeRow = (idx) => setPricing((arr) => arr.filter((_, i) => i !== idx));

  // iter55p — Copyscape pack row helpers.
  const updateCsRow = (idx, patch) => {
    setCsPacks((arr) => arr.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };
  const addCsRow = () => setCsPacks((arr) => [...arr,
    { _uid: _newUid(), id: `cs_pack_${arr.length + 1}`, name: 'New Copyscape pack', tokens: 10, price: 5, desc: '', best: false }]);
  const removeCsRow = (idx) => setCsPacks((arr) => arr.filter((_, i) => i !== idx));
  const markCsBest = (idx) => setCsPacks((arr) => arr.map((p, i) => ({ ...p, best: i === idx })));

  if (!cfg) return <section><PanelHeader title="Pricing" /><div className="mt-4 text-[12px] text-muted-foreground">Loading…</div></section>;

  return (
    <>
    <section>
      <PanelHeader title="Pricing" subtitle="Token packs and per-USD conversion. Saved immediately to all checkout sessions." onRefresh={reload} />
      <div className="mt-4 space-y-3">
        {/* iter-payments-master — Master kill-switch for Stripe. */}
        <div
          className={`rounded-lg border-2 p-4 shadow-sm transition-colors ${stripeOn ? 'border-emerald-500/60 bg-emerald-50/40' : 'border-rose-500/60 bg-rose-50/40'}`}
          data-testid="admin-payment-master-switch"
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-white ${stripeOn ? 'bg-emerald-600' : 'bg-rose-600'}`}
              data-testid="admin-payment-master-icon"
            >
              <CreditCard className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <div className="text-[15px] font-semibold">Payment Processing</div>
                <div
                  className={`font-mono-stat text-[13px] font-bold ${stripeOn ? 'text-emerald-700' : 'text-rose-700'}`}
                  data-testid="admin-payment-master-state"
                >
                  {stripeOn ? 'ON' : 'OFF'}
                </div>
              </div>
              <p className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">
                Master kill-switch for Stripe checkout. Turning this <b>OFF</b> instantly
                blocks every checkout endpoint (server returns 503) and hides every pay button app-wide — token packs, subscriptions, Copyscape, Bookstore, Weekly Maven.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={stripeOn}
              onClick={togglePayments}
              disabled={stripeSaving}
              data-testid="admin-payment-master-toggle"
              className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition ${stripeOn ? 'bg-emerald-500' : 'bg-rose-500'} ${stripeSaving ? 'opacity-60' : ''}`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${stripeOn ? 'translate-x-7' : 'translate-x-1'}`}
              />
              {stripeSaving && (
                <Loader2 className="absolute -right-6 h-3.5 w-3.5 animate-spin text-muted-foreground" />
              )}
            </button>
          </div>
          {/* iter-payments-master — Admin-editable downtime reason.
              Shown to every shopper in a red banner site-wide when
              the switch is OFF. */}
          <div className="mt-3 border-t border-border/60 pt-3">
            <div className="mb-1 text-[11.5px] font-semibold">
              Downtime reason (shown in the red site-wide banner)
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={downtimeReason}
                onChange={(e) => setDowntimeReason(e.target.value)}
                placeholder='e.g. "Card processing paused — back at 4 PM ET"'
                className="h-8 flex-1 text-[12px]"
                maxLength={220}
                data-testid="admin-payment-downtime-reason-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={saveDowntimeReason}
                disabled={downtimeSaving}
                className="h-8 gap-1 text-[11.5px]"
                data-testid="admin-payment-downtime-reason-save"
              >
                {downtimeSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Save reason
              </Button>
            </div>
            <p className="mt-1 text-[10.5px] text-muted-foreground">
              Blank = generic fallback (&quot;temporarily not accepting payments&quot;). Saved
              text is public — don&apos;t include internal ticket IDs.
            </p>
          </div>
        </div>

        <TokenPacksCard
          pricing={pricing}
          updateRow={updateRow}
          addRow={addRow}
          removeRow={removeRow}
        />

        {/* iter55p — Copyscape token-pack ladder */}
        <CopyscapePacksCard
          csPacks={csPacks}
          updateCsRow={updateCsRow}
          addCsRow={addCsRow}
          removeCsRow={removeCsRow}
          markCsBest={markCsBest}
        />

        <div className="rounded-md border border-border bg-card p-4">
          <div className="text-[12.5px] font-semibold mb-2">Custom checkout</div>
          <Field label="Tokens per USD (custom-amount checkout)" className="max-w-xs">
            <Input type="number" value={tokensPerUsd} onChange={(e) => setTokensPerUsd(parseInt(e.target.value, 10) || 0)} className="h-8 font-mono-stat text-[12px]" data-testid="admin-tokens-per-usd" />
          </Field>
        </div>

        {/* 2026-08 — Free-tier sunset date. */}
        <div className="rounded-md border border-border bg-card p-4" data-testid="admin-free-sunset-card">
          <div className="text-[12.5px] font-semibold mb-1">Free-tier sunset date</div>
          <p className="text-[11px] text-muted-foreground mb-2">
            Accounts created on or after this date get no free Claude allowance at all —
            the cheapest option becomes Basic for them. Existing free accounts (created before
            this date) are never affected. Leave blank to keep Free open indefinitely.
          </p>
          <Field label="Sunset date (UTC)" className="max-w-[180px]">
            <Input
              type="date"
              value={freeTierSunsetDate}
              onChange={(e) => setFreeTierSunsetDate(e.target.value)}
              className="h-8 font-mono-stat text-[12px]"
              data-testid="admin-free-sunset-date-input"
            />
          </Field>
          {/* 2026-10 — Live preview of exactly what a Free writer sees,
              computed from the (possibly unsaved) date above — so the
              admin can check the copy/countdown before it goes live. */}
          {freeTierSunsetDate && (() => {
            const target = new Date(freeTierSunsetDate);
            const days = Number.isNaN(target.getTime())
              ? null
              : Math.ceil((target.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
            return (
              <div className="mt-3" data-testid="admin-free-sunset-preview">
                <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Preview — what a Free writer sees
                </div>
                {days !== null && days > 0 && days <= 60 ? (
                  <FreeTierSunsetBanner previewMode previewDaysLeft={days} />
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    {days === null
                      ? 'Enter a valid date to preview the banner.'
                      : days <= 0
                        ? 'This date is in the past — the banner would never show.'
                        : `Banner only appears within 60 days of sunset (currently ${days} days out — it is not shown yet).`}
                  </p>
                )}
              </div>
            );
          })()}
        </div>

        {/* Feb-2026 COGS audit — Audiobook production + BYOK prices */}
        <div className="rounded-md border border-border bg-card p-4" data-testid="admin-audiobook-production-card">
          <div className="text-[12.5px] font-semibold mb-1 flex items-center gap-1.5">
            <Headphones className="h-3.5 w-3.5 text-primary" /> Audiobook production prices
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            Per-production Stripe checkout prices (Publisher+ tier). COGS audit (Feb 2026): TTS ~$6.60 avg novel. Standard margin 77.4 %; BYOK margin 45 % at $12.99.
          </p>
          <div className="flex flex-wrap items-end gap-4">
            <Field label="Standard production (USD)" className="max-w-[160px]">
              <Input
                type="number" min={0} max={999} step="0.01"
                value={audiobookProductionPrice}
                onChange={(e) => setAudiobookProductionPrice(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-audiobook-production-price-input"
              />
            </Field>
            <Field label="BYOK platform fee (USD)" className="max-w-[160px]">
              <Input
                type="number" min={0} max={999} step="0.01"
                value={audiobookByokFee}
                onChange={(e) => setAudiobookByokFee(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-audiobook-byok-fee-input"
              />
            </Field>
            <Field label="Fish Audio cloned voice (USD)" className="max-w-[160px]">
              <Input
                type="number" min={0} max={999} step="0.01"
                value={audiobookFishPrice}
                onChange={(e) => setAudiobookFishPrice(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-audiobook-fish-price-input"
              />
            </Field>
            <div className="pb-1 text-[11px] text-muted-foreground">
              Standard margin: {audiobookProductionPrice
                ? `${(((parseFloat(audiobookProductionPrice) - 7.91) / parseFloat(audiobookProductionPrice)) * 100).toFixed(1)}%`
                : '—'
              }
              {' · '}
              BYOK margin: {audiobookByokFee
                ? `${(((parseFloat(audiobookByokFee) - 7.19) / parseFloat(audiobookByokFee)) * 100).toFixed(1)}%`
                : '—'
              }
              {' · '}
              Fish Audio margin: {audiobookFishPrice
                ? `${(((parseFloat(audiobookFishPrice) - 3.50) / parseFloat(audiobookFishPrice)) * 100).toFixed(1)}%`
                : '—'
              }
            </div>
          </div>
        </div>

        {/* Feb-2026 COGS audit — Canva cover user-facing price */}
        <div className="rounded-md border border-border bg-card p-4" data-testid="admin-canva-cover-price-card">
          <div className="text-[12.5px] font-semibold mb-1 flex items-center gap-1.5">
            <BookOpen className="h-3.5 w-3.5 text-primary" /> Canva cover user price
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            User-facing Stripe checkout price per Canva cover design. Separate from the Canva API cost mark-up below. COGS audit margin: 86 % at $4.99 (API cost ~$0.15).
          </p>
          <div className="flex items-end gap-3">
            <Field label="Cover price (USD)" className="max-w-[140px]">
              <Input
                type="number" min={0} max={999} step="0.01"
                value={canvaCoverPrice}
                onChange={(e) => setCanvaCoverPrice(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-canva-cover-price-input"
              />
            </Field>
            <div className="pb-1 text-[11px] text-muted-foreground">
              Margin: {canvaCoverPrice
                ? `${(((parseFloat(canvaCoverPrice) - 0.70) / parseFloat(canvaCoverPrice)) * 100).toFixed(1)}%`
                : '—'
              }
            </div>
          </div>
        </div>

        {/* 2026-10 — Audiobook unlock price. */}
        <div className="rounded-md border border-border bg-card p-4" data-testid="admin-audiobook-unlock-card">
          <div className="text-[12.5px] font-semibold mb-1 flex items-center gap-1.5">
            <Headphones className="h-3.5 w-3.5 text-primary" /> Audiobook unlock price
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">
            One-time Stripe checkout price to unlock the rest of a novel's narration after the free sample chapter. OpenAI narration only — covers the ~$12.60 worst-case TTS cost of a 30-chapter novel with margin.
          </p>
          <div className="flex items-end gap-3">
            <Field label="Price (USD)" className="max-w-[140px]">
              <Input
                type="number" min={0} max={500} step="0.01"
                value={audiobookUnlockPrice}
                onChange={(e) => setAudiobookUnlockPrice(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-audiobook-unlock-price-input"
              />
            </Field>
            <div className="pb-1 text-[11px] text-muted-foreground" data-testid="admin-audiobook-unlock-preview">
              Authors see &ldquo;Unlock Full Audiobook — ${(parseFloat(audiobookUnlockPrice) || 0).toFixed(2)}&rdquo;.
            </div>
          </div>
        </div>

        {/* 2026-10 — POD length surcharge (same override treatment as the audiobook unlock price). */}
        <div className="rounded-md border border-border bg-card p-4" data-testid="admin-pod-surcharge-card">
          <div className="text-[12.5px] font-semibold mb-1 flex items-center gap-1.5">
            <BookOpen className="h-3.5 w-3.5 text-primary" /> POD length surcharge
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">
            Books over the page threshold add a per-page surcharge to the printed-copy checkout price, to protect margin on exceptionally long novels.
          </p>
          <div className="flex items-end gap-3">
            <Field label="Threshold (pages)" className="max-w-[140px]">
              <Input
                type="number" min={0} max={5000} step="1"
                value={podSurchargeThreshold}
                onChange={(e) => setPodSurchargeThreshold(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-pod-surcharge-threshold-input"
              />
            </Field>
            <Field label="Rate per page (USD)" className="max-w-[140px]">
              <Input
                type="number" min={0} max={5} step="0.01"
                value={podSurchargeRate}
                onChange={(e) => setPodSurchargeRate(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-pod-surcharge-rate-input"
              />
            </Field>
            <div className="pb-1 text-[11px] text-muted-foreground" data-testid="admin-pod-surcharge-preview">
              A {(parseInt(podSurchargeThreshold, 10) || 0) + 150}-page book adds ${(150 * (parseFloat(podSurchargeRate) || 0)).toFixed(2)}.
            </div>
          </div>
        </div>

        {/* iter-52a — Storefront default currency picker */}
        <div className="rounded-md border border-border bg-card p-4" data-testid="admin-default-currency-card">
          <div className="text-[12.5px] font-semibold mb-1">Storefront default currency</div>
          <p className="text-[11px] text-muted-foreground mb-2">
            The currency new visitors see before they pick their own preference. Stripe still charges in USD — this is purely display.
          </p>
          <Field label="Default currency" className="max-w-xs">
            <select
              value={defaultCurrency}
              onChange={(e) => setDefaultCurrency(e.target.value)}
              className="h-8 w-full rounded-md border border-input bg-background px-2 font-mono-stat text-[12px]"
              data-testid="admin-default-currency-select"
            >
              {supportedCurrencies.length === 0 && <option value="USD">USD ($) — US Dollar</option>}
              {supportedCurrencies.map(c => (
                <option key={c.code} value={c.code}>
                  {c.code} ({c.symbol}) — {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="rounded-md border border-primary/30 bg-primary/5 p-4" data-testid="admin-pricing-markup-card">
          <div className="text-[12.5px] font-semibold mb-1 flex items-center gap-1.5">
            Markup multiplier (iter-17)
            <span className="rounded-sm bg-primary/15 px-1.5 py-0 text-[10px] font-mono-stat text-primary">0-100%</span>
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">
            Single knob that adds a profit margin on top of every sell price (token packs, Copyscape packs, custom checkouts). 0 = sell at COGS, 100 = 2× the underlying cost.
          </p>
          <div className="flex items-end gap-3">
            <Field label="Markup %" className="max-w-[140px]">
              <Input
                type="number" min={0} max={100}
                value={markupPct}
                onChange={(e) => setMarkupPct(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-pricing-markup-input"
              />
            </Field>
            <div className="pb-1 text-[11px] text-muted-foreground" data-testid="admin-pricing-markup-preview">
              Example: $10 base → <b className="font-mono-stat text-primary">${(10 * (1 + (parseInt(markupPct, 10) || 0) / 100)).toFixed(2)}</b> sell.
            </div>
          </div>
        </div>


        <div className="rounded-md border border-fuchsia-300/60 bg-fuchsia-50/60 p-4" data-testid="admin-canva-pricing-card">
          <div className="text-[12.5px] font-semibold mb-1 flex items-center gap-1.5">
            Canva legacy pricing (retired)
            <span className="rounded-sm bg-amber-200 px-1.5 py-0 text-[10px] font-mono-stat text-amber-900">BYO</span>
          </div>
          <p className="text-[11px] text-muted-foreground mb-2">
            Canva pivoted to <b>Bring Your Own</b> in iter55z. Users connect their own
            Canva account under <b>Preferences → Connections</b> and every design/export
            charges their personal Canva quota — the reseller wallet has been retired.
            These fields are kept only to control the number of free signup covers.
          </p>
          <div className="grid grid-cols-12 items-end gap-3">
            <Field label="Design create COGS (¢)" className="col-span-3">
              <Input
                type="number" min={0} max={10000}
                value={canvaCreate}
                onChange={(e) => setCanvaCreate(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-canva-create-input"
              />
            </Field>
            <Field label="Export (PNG) COGS (¢)" className="col-span-3">
              <Input
                type="number" min={0} max={10000}
                value={canvaExport}
                onChange={(e) => setCanvaExport(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-canva-export-input"
              />
            </Field>
            <Field label="Markup %" className="col-span-2">
              <Input
                type="number" min={0} max={200}
                value={canvaMarkup}
                onChange={(e) => setCanvaMarkup(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-canva-markup-input"
              />
            </Field>
            <Field label="Signup free covers" className="col-span-2">
              <Input
                type="number" min={0} max={100}
                value={canvaSignupFree}
                onChange={(e) => setCanvaSignupFree(e.target.value)}
                className="h-8 font-mono-stat text-[12px]"
                data-testid="admin-canva-signup-free-input"
              />
            </Field>
            <div className="col-span-2 pb-1 text-[11px] text-muted-foreground" data-testid="admin-canva-preview">
              {(() => {
                const c = parseInt(canvaCreate, 10) || 0;
                const ex = parseInt(canvaExport, 10) || 0;
                const m = (parseInt(canvaMarkup, 10) || 0) / 100;
                const cc = Math.round(c * (1 + m));
                const ec = Math.round(ex * (1 + m));
                return (
                  <>
                    Customer pays{' '}
                    <b className="font-mono-stat text-fuchsia-900">{cc}¢</b> create +{' '}
                    <b className="font-mono-stat text-fuchsia-900">{ec}¢</b> export ={' '}
                    <b className="font-mono-stat text-fuchsia-900">{cc + ec}¢</b> per cover.
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* iter55z-i — Canva Community Pulse admin toggle */}
        <div className="rounded-md border border-fuchsia-300/60 bg-fuchsia-50/60 p-4" data-testid="admin-canva-pulse-card">
          <div className="text-[12.5px] font-semibold mb-1 flex items-center gap-1.5">
            Canva Community Pulse (social-proof widget)
            <span className={'rounded-sm px-1.5 py-0 text-[10px] font-mono-stat ' + (pulseEnabled ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground')}>
              {pulseEnabled ? 'ON' : 'OFF'}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            Shows &ldquo;N authors linked Canva this month&rdquo; on the customer landing, in the Canva cover dialog empty state, and in Preferences → Connections. Drives BYO-Canva activation.
          </p>
          <div className="grid grid-cols-12 items-end gap-3">
            <label className="col-span-3 flex items-center gap-2 text-[12px]">
              <input type="checkbox" checked={pulseEnabled}
                     onChange={(e) => setPulseEnabled(e.target.checked)}
                     data-testid="admin-canva-pulse-enabled" />
              Enable widget
            </label>
            <Field label="Display mode" className="col-span-4">
              <select
                value={pulseMode}
                onChange={(e) => setPulseMode(e.target.value)}
                disabled={!pulseEnabled}
                className="h-8 w-full rounded-sm border border-border bg-background px-2 text-[12px] disabled:opacity-50"
                data-testid="admin-canva-pulse-mode"
              >
                <option value="off">Off (hidden)</option>
                <option value="aggregate">Aggregate only (count)</option>
                <option value="anonymized">Anonymized ticker (&ldquo;An author&rdquo;)</option>
                <option value="named">Named ticker (opt-in first-name + initial)</option>
              </select>
            </Field>
            <div className="col-span-5 pb-1 text-[10.5px] text-muted-foreground" data-testid="admin-canva-pulse-hint">
              {pulseMode === 'named' && (
                <span>
                  <b className="text-fuchsia-800">Named mode:</b> only shows the name for authors who&apos;ve
                  actively opted in via <i>Preferences → Connections</i>. Non-opted rows fall back to anonymized.
                </span>
              )}
              {pulseMode === 'anonymized' && <span>Ticker rotates every 5s — never reveals identities.</span>}
              {pulseMode === 'aggregate' && <span>Just a monthly count. Zero PII, safest for launch.</span>}
              {pulseMode === 'off' && <span>Widget hidden everywhere. Backend calls still return an off payload.</span>}
            </div>
          </div>
        </div>

        <div className="rounded-md border border-border bg-card p-4" data-testid="admin-receipts-card">
          <div className="text-[12.5px] font-semibold mb-1 flex items-center gap-1.5">
            Email receipts (Resend)
            <span className={'rounded-sm px-1.5 py-0 text-[10px] font-mono-stat ' + (receiptsEnabled ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground')}>
              {receiptsEnabled ? 'ON' : 'OFF'}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            Send a branded receipt to the buyer after every successful Stripe payment (token packs, Copyscape packs, and subscription renewals). Requires <code>RESEND_API_KEY</code> in the backend env.
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="flex items-center gap-2 text-[12px]">
              <input type="checkbox" checked={receiptsEnabled}
                     onChange={(e) => setReceiptsEnabled(e.target.checked)}
                     data-testid="admin-receipts-enabled" />
              Enable receipt emails
            </label>
            <Field label="Brand name (header)">
              <Input value={receiptsBrand} onChange={(e) => setReceiptsBrand(e.target.value)}
                     className="h-8 text-[12px]" data-testid="admin-receipts-brand"
                     placeholder="The Loom V2.38_f55" />
            </Field>
            <Field label="Header color (hex)">
              <div className="flex items-center gap-2">
                <Input type="color" value={receiptsColor.length === 7 ? receiptsColor : '#18181b'}
                       onChange={(e) => setReceiptsColor(e.target.value)}
                       className="h-8 w-12 cursor-pointer p-1"
                       data-testid="admin-receipts-color-swatch" />
                <Input value={receiptsColor} onChange={(e) => setReceiptsColor(e.target.value)}
                       className="h-8 font-mono-stat text-[12px]"
                       placeholder="#18181b" data-testid="admin-receipts-color" />
              </div>
            </Field>
            <Field label="Subject prefix" className="md:col-span-2">
              <Input value={receiptsSubject} onChange={(e) => setReceiptsSubject(e.target.value)}
                     className="h-8 text-[12px]" data-testid="admin-receipts-subject"
                     placeholder="The Loom · Receipt" />
            </Field>
            <Field label="Footer HTML (optional, ≤2000 chars)" className="md:col-span-2">
              <textarea value={receiptsFooter}
                         onChange={(e) => setReceiptsFooter(e.target.value)}
                         rows={3} maxLength={2000}
                         data-testid="admin-receipts-footer"
                         className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono-stat text-[11.5px]"
                         placeholder="<a href='mailto:support@example.com'>Contact support</a>" />
            </Field>
          </div>
        </div>

        <div className="rounded-md border border-border bg-card p-4" data-testid="admin-promo-card">
          <div className="text-[12.5px] font-semibold mb-1 flex items-center gap-1.5">
            Yearly subscription promo badge
            <span className={'rounded-sm px-1.5 py-0 text-[10px] font-mono-stat ' + (promoEnabled ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground')}>
              {promoEnabled ? 'ON' : 'OFF'}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            Renders a small badge next to the &ldquo;Yearly&rdquo; cadence toggle on the Subscribe tab. Leave label blank to auto-compute &ldquo;Save N%&rdquo; from your monthly × 12 vs yearly prices on the best plan.
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="flex items-center gap-2 text-[12px]">
              <input type="checkbox" checked={promoEnabled}
                     onChange={(e) => setPromoEnabled(e.target.checked)}
                     data-testid="admin-promo-enabled" />
              Show yearly promo badge
            </label>
            <Field label="Custom label (optional)">
              <Input value={promoLabel}
                     onChange={(e) => setPromoLabel(e.target.value)}
                     className="h-8 text-[12px]"
                     placeholder="auto: Save N%"
                     data-testid="admin-promo-label" />
            </Field>
          </div>
        </div>

        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={busy} className="gap-1.5" data-testid="admin-pricing-save"><Save className="h-3.5 w-3.5" /> Save pricing</Button>
        </div>
      </div>
    </section>

    {/* Founding Member Promo config — deadline and cap are editable here
        so we don't have to redeploy just to extend the offer. */}
    <FoundingPromoConfigCard />

    {/* iter-promo — Writer's Launch campaign card */}
    <section>
      <PanelHeader
        icon={<Settings2 className="h-4 w-4" />}
        title="Writer's Launch Campaign"
        subtitle="Promo tier: auto-assigned to new signups during the active window. 75k tokens, Pro capabilities, 3–5 chapters free."
      />
      <div className="mt-4">
        <PromoAdminCard />
      </div>
    </section>

    {/* iter-promo — Tier feature map + per-tier COGS popup */}
    <section>
      <PanelHeader
        icon={<LayoutList className="h-4 w-4" />}
        title="Tier Feature Map"
        subtitle="All features per tier. Click 'Edit Features' on any tier to toggle COGS-contributing capabilities and see associated costs."
      />
      <div className="mt-4 space-y-3">
        {/* Quick-access edit buttons per tier — Feb-2026 consolidated names */}
        <div className="flex flex-wrap gap-2">
          {['free','promo','author','publisher','studio'].map(t => (
            <button
              key={t}
              data-testid={`edit-features-${t}`}
              onClick={() => setFeaturesPopupTier({ id: t, name: t.charAt(0).toUpperCase() + t.slice(1) })}
              className="rounded-lg border border-zinc-700/50 bg-zinc-800/60 px-3 py-1.5 text-xs text-zinc-300 hover:border-violet-500/50 hover:text-white transition-colors"
            >
              Edit {t}
            </button>
          ))}
        </div>
        <TierFeatureMap adminMode initiallyOpen />
      </div>
    </section>

    {featuresPopupTier && (
      <TierFeaturesPopup
        tier={featuresPopupTier}
        onClose={() => setFeaturesPopupTier(null)}
      />
    )}
    </>
  );
}

/**
 * FoundingPromoConfigCard
 * ────────────────────────
 * Lets admins change the founding-promo deadline and redemption cap
 * without re-deploying. Reads GET /api/admin/founding-promo/config and
 * writes back via PUT. The "Only N spots left — ends Dec 16" badge in
 * PaywallDialog reads `redeem_by` from GET /subscriptions/founding-promo
 * which now pulls from the DB via founding_promo.get_config().
 */
function FoundingPromoConfigCard() {
  const [cfg,   setCfg]   = useState(null);
  const [busy,  setBusy]  = useState(false);
  const [dirty, setDirty] = useState(false);
  const [maxR,     setMaxR]     = useState('');
  const [redeemBy, setRedeemBy] = useState('');

  useEffect(() => {
    adminApi.getFoundingPromoConfig()
      .then((r) => {
        setCfg(r);
        setMaxR(String(r.max_redemptions));
        setRedeemBy((r.redeem_by || '').slice(0, 10));
      })
      .catch(() => {});
  }, []);

  const save = async () => {
    if (!redeemBy || !maxR) return;
    setBusy(true);
    try {
      const iso = `${redeemBy}T23:59:59+00:00`;
      await adminApi.saveFoundingPromoConfig({
        max_redemptions: parseInt(maxR, 10),
        redeem_by: iso,
      });
      toast.success('Founding promo config saved.');
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return null;

  const displayDeadline = cfg.redeem_by
    ? new Date(cfg.redeem_by).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  return (
    <section className="space-y-4" data-testid="founding-promo-config-card">
      <PanelHeader
        icon={<Gift className="h-4 w-4" />}
        title="Founding Member Promo"
        subtitle={`Code: ${cfg.code ?? 'FOUNDING35'} · ${cfg.percent_off ?? 35}% off × ${cfg.months ?? 3} months · deadline: ${displayDeadline}`}
      />
      <div className="space-y-3 rounded-md border border-border bg-card p-4">
        <p className="text-[11.5px] text-muted-foreground">
          Editing these values updates the live PaywallDialog countdown badge and Stripe coupon
          parameters without a re-deploy. The "Only N spots left" label and deadline date both
          pull from these fields.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Redemption cap" hint="Total signups allowed (Stripe max_redemptions)">
            <Input
              type="number" min={1} max={10000}
              value={maxR}
              onChange={(e) => { setMaxR(e.target.value); setDirty(true); }}
              className="h-8 text-[12px]"
              data-testid="admin-founding-max-redemptions"
            />
          </Field>
          <Field label="Deadline date" hint="Promo ends end-of-day UTC on this date">
            <Input
              type="date"
              value={redeemBy}
              onChange={(e) => { setRedeemBy(e.target.value); setDirty(true); }}
              className="h-8 text-[12px]"
              data-testid="admin-founding-redeem-by"
            />
          </Field>
        </div>
        <div className="flex items-center gap-3">
          <Button
            size="sm" onClick={save} disabled={busy || !dirty}
            className="gap-1.5" data-testid="admin-founding-save-btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save promo config
          </Button>
          {!dirty && (
            <span className="text-[11px] text-muted-foreground">
              Cap: <strong>{maxR}</strong> · Deadline: <strong>{redeemBy}</strong>
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

